export const DocumentType = {
  legal: 'legal',
  shared: 'shared',
  evidence: 'evidence',
  unlinked: 'unlinked',
} as const;

export type DocumentType = (typeof DocumentType)[keyof typeof DocumentType];

export const DocumentStatus = {
  missing_signature: 'missing_signature',
  in_progress: 'in_progress',
  signed: 'signed',
} as const;

export type DocumentStatus = (typeof DocumentStatus)[keyof typeof DocumentStatus];

export const DocumentMetaStatus = {
  original: 'original',
  prepared: 'prepared',
  reviewed: 'reviewed',
} as const;

export type DocumentMetaStatus = (typeof DocumentMetaStatus)[keyof typeof DocumentMetaStatus];

export const DocumentAccessType = {
  public: 'public',
  private: 'private',
} as const;

export type DocumentAccessType = (typeof DocumentAccessType)[keyof typeof DocumentAccessType];

export const DocumentAccessRole = {
  owner: 'owner',
  read_only: 'read_only',
  editor: 'editor',
} as const;

export type DocumentAccessRole = (typeof DocumentAccessRole)[keyof typeof DocumentAccessRole];

export const DocumentLegalType = {
  offer: 'offer',
  engagement_letter: 'engagement_letter',
  letter_of_acceptance: 'letter_of_acceptance',
  letter_of_representation: 'letter_of_representation',
  audit_report: 'audit_report',
  invoice: 'invoice',
  archiving_report: 'archiving_report',
} as const;

export type DocumentLegalType = (typeof DocumentLegalType)[keyof typeof DocumentLegalType];

export const DocumentSignaturesStatus = {
  failed: 'failed',
  unsigned: 'unsigned',
  in_progress: 'in_progress',
  signed: 'signed',
} as const;

export type DocumentSignaturesStatus =
  (typeof DocumentSignaturesStatus)[keyof typeof DocumentSignaturesStatus];

export const DocumentAnnotationType = {
  tickmark: 'tickmark',
  range_tickmark: 'range_tickmark',
  rectangle: 'rectangle',
  line: 'line',
  arrow: 'arrow',
  highlighter: 'highlighter',
  document_note: 'document_note',
  id_note: 'id_note',
  comment: 'comment',
  finding: 'finding',
  document_tag: 'document_tag',
  reference_tag: 'reference_tag',
  reconciliation: 'reconciliation',
  signature_request: 'signature_request',
  status_prepared: 'status_prepared',
  status_reviewed: 'status_reviewed',
} as const;

export type DocumentAnnotationType =
  (typeof DocumentAnnotationType)[keyof typeof DocumentAnnotationType];

export const CompanyMemberSignatory = {
  sole: 'sole',
  joint: 'joint',
} as const;

export type CompanyMemberSignatory =
  (typeof CompanyMemberSignatory)[keyof typeof CompanyMemberSignatory];

export const CompanyType = {
  client: 'client',
  audit_firm: 'audit_firm',
  fiduciary: 'fiduciary',
} as const;

export type CompanyType = (typeof CompanyType)[keyof typeof CompanyType];

export const TeamType = {
  audit_firm: 'audit_firm',
  fiduciary: 'fiduciary',
} as const;

export type TeamType = (typeof TeamType)[keyof typeof TeamType];

export const RequestType = {
  file_request: 'file_request',
  data_transfer_request: 'data_transfer_request',
} as const;

export type RequestType = (typeof RequestType)[keyof typeof RequestType];

export const AccountingStandard = {
  OR: 'OR',
  GAAP: 'GAAP',
  IFRS: 'IFRS',
} as const;

export type AccountingStandard = (typeof AccountingStandard)[keyof typeof AccountingStandard];

export const AuditKind = {
  auditor: 'auditor',
  fiduciary: 'fiduciary',
} as const;

export type AuditKind = (typeof AuditKind)[keyof typeof AuditKind];

export enum NotificationType {
  status_update = 'status_update',
  document_upload = 'document_upload',
  comment_added = 'comment_added',
  comment_mention = 'comment_mention',
  request_created = 'request_created',
}

export enum NotificationAction {
  read = 'read',
  click = 'click',
}

export const RequestStatus = {
  SENT: 'sent',
  REQUESTED: 'requested',
  APPROVED: 'approved',
  REJECTED: 'rejected',
} as const;

export type RequestStatus = (typeof RequestStatus)[keyof typeof RequestStatus];

export const RequestFlowStatus = {
  draft: 'draft',
  requested: 'requested',
  cancelled: 'cancelled',
  completed: 'completed',
} as const;

export type RequestFlowStatus = (typeof RequestFlowStatus)[keyof typeof RequestFlowStatus];

export const RequestContainerVisibility = {
  unpublished: 'unpublished',
  published: 'published',
} as const;

export type RequestContainerVisibility =
  (typeof RequestContainerVisibility)[keyof typeof RequestContainerVisibility];

export const RequestContainerClientAccess = {
  everyone: 'everyone',
  assigned_only: 'assigned_only',
} as const;

export type RequestContainerClientAccess =
  (typeof RequestContainerClientAccess)[keyof typeof RequestContainerClientAccess];

export const RequestBlockStatus = {
  requested: 'requested',
  marked_done: 'marked_done',
  returned: 'returned',
  confirmed: 'confirmed',
} as const;

export const RequestBlockType = {
  short_answer: 'short_answer',
  long_answer: 'long_answer',
  number: 'number',
  date: 'date',
  file_upload: 'file_upload',
  single_choice: 'single_choice',
  multiple_choice: 'multiple_choice',
  contact: 'contact',
  data_access_bexio: 'data_access_bexio',
  accounting_system: 'accounting_system',
  industry: 'industry',
} as const;

export type RequestBlockType = (typeof RequestBlockType)[keyof typeof RequestBlockType];

export const RequestFlowTemplateShareRole = {
  owner: 'owner',
  collaborator: 'collaborator',
  member: 'member',
} as const;

export type RequestFlowTemplateShareRole =
  (typeof RequestFlowTemplateShareRole)[keyof typeof RequestFlowTemplateShareRole];

export const RequestFlowTemplateVersionStatus = {
  draft: 'draft',
  pending_review: 'pending_review',
  approved: 'approved',
  rejected: 'rejected',
} as const;

export type RequestFlowTemplateVersionStatus =
  (typeof RequestFlowTemplateVersionStatus)[keyof typeof RequestFlowTemplateVersionStatus];

export const ReviewDecision = {
  approved: 'approved',
  rejected: 'rejected',
} as const;

export type ReviewDecision = (typeof ReviewDecision)[keyof typeof ReviewDecision];

export const TemplateVersionCommentType = {
  change_note: 'change_note',
  review_comment: 'review_comment',
} as const;

export type TemplateVersionCommentType =
  (typeof TemplateVersionCommentType)[keyof typeof TemplateVersionCommentType];

export const TemplateVersionActivityType = {
  version_created: 'version_created',
  version_updated: 'version_updated',
  version_status_changed: 'version_status_changed',
} as const;

export type TemplateVersionActivityType =
  (typeof TemplateVersionActivityType)[keyof typeof TemplateVersionActivityType];

export type RequestBlockStatus = (typeof RequestBlockStatus)[keyof typeof RequestBlockStatus];

export const CompanyMemberRole = {
  auditor: 'auditor',
  main_admin: 'main_admin',
  admin: 'admin',
  member: 'member',
} as const;

export type CompanyMemberRole = (typeof CompanyMemberRole)[keyof typeof CompanyMemberRole];

// Platform-level user role (user.role)
export const MemberRole = {
  SUPER_ADMIN: 'super-admin',
  USER: 'user',
  AUDITOR: 'auditor',
  ADMIN: 'admin',
  MEMBER: 'member',
} as const;

export type MemberRole = (typeof MemberRole)[keyof typeof MemberRole];

export const StatusStepCategory = {
  core: 'core',
  additional: 'additional',
} as const;

export type StatusStepCategory = (typeof StatusStepCategory)[keyof typeof StatusStepCategory];

export const StatusStepType = {
  simple: 'simple',
  request: 'request',
  signature: 'signature',
  meeting: 'meeting',
  inquiry: 'inquiry',
  analytics: 'analytics',
  test_of_details: 'test_of_details',
  general: 'general',
  bookkeeping: 'bookkeeping',
  controls: 'controls',
  reporting: 'reporting',
} as const;

export type StatusStepType = (typeof StatusStepType)[keyof typeof StatusStepType];

export const StatusStepOwnership = {
  client: 'client',
  auditor: 'auditor',
} as const;

export type StatusStepOwnership = (typeof StatusStepOwnership)[keyof typeof StatusStepOwnership];

export const StepStatus = {
  not_started: 'not_started',
  in_progress: 'in_progress',
  pending: 'pending',
  done: 'done',
  blocked: 'blocked',
  not_required: 'not_required',
} as const;

export type StepStatus = (typeof StepStatus)[keyof typeof StepStatus];

// User signatory status for document signing
export const SignatoryStatus = {
  pending: 'pending',
  verified: 'verified',
  rejected: 'rejected',
} as const;

export type SignatoryStatus = (typeof SignatoryStatus)[keyof typeof SignatoryStatus];

// Auditor team member roles (note: matches AudiorTeamMemberRole in Prisma - typo in backend)
export const AuditorTeamMemberRole = {
  // auditor kind roles
  lead_auditor: 'lead_auditor',
  auditor: 'auditor',
  quality_assurance: 'quality_assurance',
  quality_review: 'quality_review',
  // fiduciary kind roles
  lead_accountant: 'lead_accountant',
  accountant: 'accountant',
} as const;

export type AuditorTeamMemberRole =
  (typeof AuditorTeamMemberRole)[keyof typeof AuditorTeamMemberRole];

// Audit workflow status
export const AuditStatus = {
  offer: 'offer',
  election: 'election',
  planning: 'planning',
  execution: 'execution',
  reporting: 'reporting',
} as const;

export type AuditStatus = (typeof AuditStatus)[keyof typeof AuditStatus];

// Audit type (not an enum in backend, but string with known values)
export const AuditType = {
  annual: 'annual',
  interim: 'interim',
  special: 'special',
} as const;

export type AuditType = (typeof AuditType)[keyof typeof AuditType];
